import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    ButtonBase,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Step,
    StepLabel,
    Stepper,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Close, Delete, ExpandMore, LocalShipping, Tune } from "@material-ui/icons"
import React, { useEffect, useState } from "react"
import { useSnackbar } from "../../../utils/notificacoes"
import { useCupomEstabelecimentoFornecedorContext } from "../context/useCupomEstabelecimentoFornecedorContext"
import type { CupomItem } from "../models/cupomEstabelecimentoFornecedorModel"
import type { TipoDesconto } from "./cupomUtils"
import {
    aplicarTipo,
    estaExpirado,
    formatarData,
    novoCupom,
    tipoDoCupom,
    validarCodigo,
    validarCupom,
    validarDesconto,
} from "./cupomUtils"

const VERDE = "#4caf50"

const useStyles = makeStyles(theme => ({
    titulo: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        paddingRight: theme.spacing(6),
    },
    fechar: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
    },
    secao: {
        marginBottom: theme.spacing(3),
    },
    secaoTitulo: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(0.5),
    },
    secaoAjuda: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(1.5),
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "&:hover fieldset": { borderColor: VERDE },
            "&.Mui-focused fieldset": { borderColor: VERDE },
        },
        "& label.Mui-focused": { color: VERDE },
    },
    codigoInput: {
        fontSize: "1.4rem",
        fontWeight: 600,
        letterSpacing: 1,
    },
    tipoOpcao: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: theme.spacing(2, 1),
        borderRadius: 12,
        border: "2px solid #e0e0e0",
        backgroundColor: "white",
        transition: "border-color .15s, background-color .15s",
        "&:hover": { borderColor: "#a5d6a7" },
    },
    tipoOpcaoAtiva: {
        borderColor: VERDE,
        backgroundColor: "rgba(var(--cor-botao-salvar-rgb),0.08)",
        "&:hover": { borderColor: VERDE },
    },
    tipoIcone: {
        fontSize: "1.6rem",
        fontWeight: 700,
        color: VERDE,
        lineHeight: "32px",
        height: 32,
    },
    tipoNome: {
        fontWeight: 600,
        color: "#2c3e50",
        marginTop: theme.spacing(0.5),
    },
    tipoDesc: {
        color: "#7f8c8d",
        fontSize: "0.75rem",
    },
    segmentado: {
        display: "flex",
        gap: theme.spacing(1),
        flexWrap: "wrap",
    },
    segmento: {
        borderRadius: 20,
        textTransform: "none",
        borderColor: "#e0e0e0",
        color: "#2c3e50",
    },
    segmentoAtivo: {
        borderColor: VERDE,
        backgroundColor: "rgba(var(--cor-botao-salvar-rgb),0.1)",
        color: "#2e7d32",
        fontWeight: 600,
        "&:hover": { backgroundColor: "rgba(var(--cor-botao-salvar-rgb),0.16)" },
    },
    avancadas: {
        borderRadius: "12px !important",
        border: "1px solid #e9ecef",
        boxShadow: "none",
        "&:before": { display: "none" },
    },
    switchVerde: {
        "& .Mui-checked": { color: VERDE },
        "& .Mui-checked + .MuiSwitch-track": { backgroundColor: VERDE },
    },
    botaoPrimario: {
        borderRadius: 20,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        padding: theme.spacing(0.75, 3),
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    botaoExcluir: {
        borderRadius: 20,
        borderColor: "var(--cor-erro)",
        color: "var(--cor-erro)",
        "&:hover": { backgroundColor: "rgba(231,76,60,0.08)" },
    },
    avisoExpirado: {
        backgroundColor: "#fff4e5",
        border: "1px solid #ffcc80",
        color: "#8a5300",
        borderRadius: 8,
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(3),
    },
    acoes: {
        padding: theme.spacing(1.5, 3),
        borderTop: "1px solid #e9ecef",
    },
}))

type Atualizar = <K extends keyof CupomItem>(campo: K, valor: CupomItem[K]) => void

function numeroOuNull(v: string) {
    return v !== "" ? Number(v.replace(",", ".")) : null
}

function Secao({ titulo, ajuda, children }: { titulo: string; ajuda?: string; children: React.ReactNode }) {
    const classes = useStyles()
    return (
        <div className={classes.secao}>
            <Typography className={classes.secaoTitulo}>{titulo}</Typography>
            {ajuda && (
                <Typography variant="body2" className={classes.secaoAjuda}>
                    {ajuda}
                </Typography>
            )}
            {children}
        </div>
    )
}

function Segmentado<T extends string | number | boolean>({
    valor,
    opcoes,
    onChange,
    disabled,
}: {
    valor: T
    opcoes: Array<{ valor: T; label: string }>
    onChange: (v: T) => void
    disabled?: boolean
}) {
    const classes = useStyles()
    return (
        <div className={classes.segmentado}>
            {opcoes.map(o => (
                <Button
                    key={String(o.valor)}
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    className={`${classes.segmento} ${o.valor === valor ? classes.segmentoAtivo : ""}`}
                    onClick={() => onChange(o.valor)}
                >
                    {o.label}
                </Button>
            ))}
        </div>
    )
}

function Chave({
    label,
    ajuda,
    checked,
    onChange,
    disabled,
}: {
    label: string
    ajuda?: string
    checked: boolean
    onChange: (v: boolean) => void
    disabled?: boolean
}) {
    const classes = useStyles()
    return (
        <FormControlLabel
            className={classes.switchVerde}
            control={
                <Switch
                    checked={checked}
                    onChange={e => {
                        const v = e.target.checked
                        onChange(v)
                    }}
                    disabled={disabled}
                />
            }
            label={
                <Box>
                    <Typography variant="body2">{label}</Typography>
                    {ajuda && (
                        <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                            {ajuda}
                        </Typography>
                    )}
                </Box>
            }
        />
    )
}

function SecaoCodigo({ cupom, atualizar, erro, disabled, autoFocus }: {
    cupom: CupomItem
    atualizar: Atualizar
    erro: string | null
    disabled?: boolean
    autoFocus?: boolean
}) {
    const classes = useStyles()
    return (
        <Secao titulo="Código do cupom" ajuda="É o código que o cliente vai digitar (ou ver) para usar o desconto.">
            <TextField
                className={classes.textField}
                variant="outlined"
                fullWidth
                autoFocus={autoFocus}
                placeholder="EX: BEMVINDO10"
                value={cupom.nomeCupom}
                onChange={e => {
                    const v = e.target.value
                    atualizar("nomeCupom", v.toUpperCase().replace(/\s/g, ""))
                }}
                error={!!erro && !!cupom.nomeCupom}
                helperText={cupom.nomeCupom ? erro ?? " " : " "}
                InputProps={{ classes: { input: classes.codigoInput } }}
                disabled={disabled}
            />
        </Secao>
    )
}

const TIPOS: Array<{ tipo: TipoDesconto; icone: React.ReactNode; nome: string; desc: string }> = [
    { tipo: "percentual", icone: "%", nome: "Percentual", desc: "Desconto em % sobre o pedido" },
    { tipo: "valor", icone: "R$", nome: "Valor fixo", desc: "Desconto de um valor em reais" },
    { tipo: "frete", icone: <LocalShipping />, nome: "Frete grátis", desc: "Apenas zera a taxa de entrega" },
]

function SecaoDesconto({ cupom, atualizar, setCupom, disabled }: {
    cupom: CupomItem
    atualizar: Atualizar
    setCupom: (c: CupomItem) => void
    disabled?: boolean
}) {
    const classes = useStyles()
    const tipo = tipoDoCupom(cupom)
    return (
        <Secao titulo="Tipo de desconto" ajuda="O cupom vai dar um desconto percentual, um valor fixo ou apenas frete grátis?">
            <Grid container spacing={1}>
                {TIPOS.map(t => (
                    <Grid item xs={4} key={t.tipo}>
                        <ButtonBase
                            className={`${classes.tipoOpcao} ${tipo === t.tipo ? classes.tipoOpcaoAtiva : ""}`}
                            onClick={() => setCupom(aplicarTipo(cupom, t.tipo))}
                            disabled={disabled}
                        >
                            <span className={classes.tipoIcone}>{t.icone}</span>
                            <Typography variant="body2" className={classes.tipoNome}>
                                {t.nome}
                            </Typography>
                            <Typography className={classes.tipoDesc}>{t.desc}</Typography>
                        </ButtonBase>
                    </Grid>
                ))}
            </Grid>

            {tipo !== "frete" && (
                <Grid container spacing={2} style={{ marginTop: 8 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            className={classes.textField}
                            label={tipo === "percentual" ? "Percentual de desconto" : "Valor do desconto"}
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            value={cupom.valor || ""}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("valor", numeroOuNull(v) ?? 0)
                            }}
                            error={!!validarDesconto(cupom)}
                            helperText={validarDesconto(cupom) ?? " "}
                            InputProps={
                                tipo === "percentual"
                                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                                    : { startAdornment: <InputAdornment position="start">R$</InputAdornment> }
                            }
                            disabled={disabled}
                        />
                    </Grid>
                    {tipo === "percentual" && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                className={classes.textField}
                                label="Desconto máximo (opcional)"
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="number"
                                value={cupom.maximoDesconto ?? ""}
                                onChange={e => {
                                    const v = e.target.value
                                    atualizar("maximoDesconto", numeroOuNull(v))
                                }}
                                helperText="Limite em R$ do desconto"
                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                disabled={disabled}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </Secao>
    )
}

function SecaoRegras({ cupom, atualizar, disabled, fimSalvo }: {
    cupom: CupomItem
    atualizar: Atualizar
    disabled?: boolean
    /** fim de validade gravado no backend (só na edição) */
    fimSalvo?: number | null
}) {
    const classes = useStyles()
    const fimCupomFormatado = cupom.fimCupomTimestamp
        ? new Date(cupom.fimCupomTimestamp).toISOString().split("T")[0]
        : ""
    return (
        <>
            <Secao
                titulo="Forma de aplicação"
                ajuda={
                    cupom.automatico
                        ? "O desconto é aplicado sozinho no pedido, sem o cliente digitar o código."
                        : "O cliente precisa digitar o código para ganhar o desconto."
                }
            >
                <Segmentado
                    valor={cupom.automatico}
                    opcoes={[
                        { valor: true, label: "Automática" },
                        { valor: false, label: "Digitada pelo cliente" },
                    ]}
                    onChange={v => atualizar("automatico", v)}
                    disabled={disabled}
                />
            </Secao>

            <Secao titulo="Válido para">
                <Segmentado
                    valor={cupom.apenasPrimeiraCompra}
                    opcoes={[
                        { valor: true, label: "Primeira compra" },
                        { valor: false, label: "Todas as compras" },
                    ]}
                    onChange={v => atualizar("apenasPrimeiraCompra", v)}
                    disabled={disabled}
                />
            </Secao>

            <Secao
                titulo="Validade"
                ajuda={
                    fimSalvo
                        ? `${fimSalvo < Date.now() ? "Expirou em" : "Válido até"} ${formatarData(fimSalvo)}.` +
                          (cupom.validadeDias !== 0 ? " Ao salvar, a validade é recontada a partir de hoje." : "")
                        : undefined
                }
            >
                <RadioGroup
                    row
                    value={cupom.validadeDias !== null ? String(cupom.validadeDias) : ""}
                    onChange={e => {
                        const v = e.target.value
                        atualizar("validadeDias", v !== "" ? Number(v) : null)
                    }}
                >
                    <FormControlLabel value="1" control={<Radio size="small" disabled={disabled} />} label="1 dia" />
                    <FormControlLabel value="7" control={<Radio size="small" disabled={disabled} />} label="7 dias" />
                    <FormControlLabel value="30" control={<Radio size="small" disabled={disabled} />} label="30 dias" />
                    <FormControlLabel value="3650" control={<Radio size="small" disabled={disabled} />} label="Indeterminado" />
                    <FormControlLabel value="0" control={<Radio size="small" disabled={disabled} />} label="Personalizado" />
                </RadioGroup>
                {cupom.validadeDias === 0 && (
                    <TextField
                        className={classes.textField}
                        label="Válido até"
                        variant="outlined"
                        size="small"
                        type="date"
                        style={{ marginTop: 8, minWidth: 200 }}
                        InputLabelProps={{ shrink: true }}
                        value={fimCupomFormatado}
                        onChange={e => {
                            const dateStr = e.target.value
                            atualizar("fimCupomTimestamp", dateStr ? new Date(dateStr).getTime() : null)
                        }}
                        error={!cupom.fimCupomTimestamp}
                        disabled={disabled}
                    />
                )}
            </Secao>

            <Secao titulo="Pedido mínimo">
                <TextField
                    className={classes.textField}
                    label="Valor mínimo do pedido (opcional)"
                    variant="outlined"
                    size="small"
                    type="number"
                    style={{ minWidth: 260 }}
                    value={cupom.valorMinimoPedido ?? ""}
                    onChange={e => {
                        const v = e.target.value
                        atualizar("valorMinimoPedido", numeroOuNull(v))
                    }}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    disabled={disabled}
                />
            </Secao>

            <Chave
                label="Cupom ativo"
                ajuda="Desative para pausar o cupom sem excluí-lo."
                checked={cupom.ativo}
                onChange={v => atualizar("ativo", v)}
                disabled={disabled}
            />
        </>
    )
}

function SecaoAvancadas({ cupom, atualizar, disabled }: { cupom: CupomItem; atualizar: Atualizar; disabled?: boolean }) {
    const classes = useStyles()
    const tipo = tipoDoCupom(cupom)
    return (
        <Accordion className={classes.avancadas}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Tune style={{ color: VERDE }} />
                    <Typography style={{ fontWeight: 600, color: "#2c3e50" }}>Configurações avançadas</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            label="Usos por cliente"
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            value={cupom.quantidadePorCliente}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("quantidadePorCliente", v !== "" ? Number(v) : 1)
                            }}
                            helperText="Quantas vezes cada cliente pode usar"
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            label="Limite total de usos"
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            value={cupom.quantidadeMaxima ?? ""}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("quantidadeMaxima", v !== "" ? Number(v) : null)
                            }}
                            helperText="Opcional · somando todos os clientes"
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Disponível em"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.appSiteTodos}
                            onChange={e => {
                                const v = Number(e.target.value)
                                atualizar("appSiteTodos", v)
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value={0}>App e Site</MenuItem>
                            <MenuItem value={1}>Somente App</MenuItem>
                            <MenuItem value={2}>Somente Site</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Chave
                            label="Exigir endereço único"
                            ajuda="Um uso por endereço de entrega"
                            checked={cupom.exigirEnderecoUnicoParaDesconto}
                            onChange={v => atualizar("exigirEnderecoUnicoParaDesconto", v)}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Chave
                            label="Exigir CPF"
                            ajuda="Cliente precisa informar o CPF"
                            checked={cupom.exigirCpfParaDesconto}
                            onChange={v => atualizar("exigirCpfParaDesconto", v)}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Chave
                            label="Somente horário de almoço"
                            checked={cupom.horarioAlmoco}
                            onChange={v => atualizar("horarioAlmoco", v)}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Chave
                            label="Visível na tela inicial"
                            checked={cupom.visivelTelaInicial}
                            onChange={v => atualizar("visivelTelaInicial", v)}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Chave
                            label="Aplicar apenas a clientes definidos"
                            checked={cupom.olharListaClientes}
                            onChange={v => atualizar("olharListaClientes", v)}
                            disabled={disabled}
                        />
                    </Grid>
                    {tipo !== "frete" && (
                        <Grid item xs={12} sm={6}>
                            <Chave
                                label="Também dar frete grátis"
                                ajuda="Além do desconto, zera a taxa de entrega"
                                checked={cupom.freteGratis}
                                onChange={v => atualizar("freteGratis", v)}
                                disabled={disabled}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                        <TextField
                            className={classes.textField}
                            label="Telefone do cliente (opcional)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="xx xxxxx-xxxx"
                            value={cupom.telefoneCliente ?? ""}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("telefoneCliente", v || null)
                            }}
                            helperText="Restringe o cupom a um único cliente"
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            className={classes.textField}
                            label="Regulamento / descrição (opcional)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            value={cupom.descricao}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("descricao", v)
                            }}
                            disabled={disabled}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

const PASSOS = ["Código", "Desconto", "Regras"]

export function CupomDialog({
    aberto,
    cupomEdicao,
    onFechar,
}: {
    aberto: boolean
    /** null = cadastro de novo cupom */
    cupomEdicao: CupomItem | null
    onFechar: () => void
}) {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"))
    const { dados, salvarCupom, excluirCupom, erroCarregamento } = useCupomEstabelecimentoFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const novo = cupomEdicao === null
    const [cupom, setCupom] = useState<CupomItem>(() => cupomEdicao ?? novoCupom(dados._nextLocalId))
    const [passo, setPasso] = useState(0)
    const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)
    const [salvando, setSalvando] = useState(false)

    useEffect(() => {
        if (!aberto) return
        setCupom(cupomEdicao ?? novoCupom(dados._nextLocalId))
        setPasso(0)
        setConfirmandoExclusao(false)
    }, [aberto, cupomEdicao])

    const atualizar: Atualizar = (campo, valor) => setCupom(c => ({ ...c, [campo]: valor }))
    const disabled = !!erroCarregamento || salvando
    const erroCodigo = validarCodigo(cupom, dados.cupons)

    const avancar = () => {
        const erro = passo === 0 ? erroCodigo : validarDesconto(cupom)
        if (erro) {
            enqueueSnackbar(erro, { variant: "error" })
            return
        }
        setPasso(p => p + 1)
    }

    const handleSalvar = async () => {
        const erro = validarCupom(cupom, dados.cupons)
        if (erro) {
            enqueueSnackbar(erro, { variant: "error" })
            return
        }
        try {
            setSalvando(true)
            await salvarCupom(cupom)
            enqueueSnackbar(novo ? "Cupom criado com sucesso!" : "Cupom salvo com sucesso!", { variant: "success" })
            onFechar()
        } catch (error) {
            enqueueSnackbar((error as Error).message || "Erro ao salvar cupom.", { variant: "error" })
        } finally {
            setSalvando(false)
        }
    }

    const handleExcluir = async () => {
        try {
            setSalvando(true)
            await excluirCupom(cupom)
            enqueueSnackbar("Cupom excluído.", { variant: "success" })
            onFechar()
        } catch (error) {
            enqueueSnackbar((error as Error).message || "Erro ao excluir cupom.", { variant: "error" })
        } finally {
            setSalvando(false)
        }
    }

    return (
        <Dialog open={aberto} onClose={salvando ? undefined : onFechar} fullWidth maxWidth="sm" fullScreen={isMobile}>
            <DialogTitle disableTypography className={classes.titulo}>
                <Typography variant="h6" style={{ fontWeight: 600, color: "#2c3e50" }}>
                    {novo ? "Novo cupom" : cupomEdicao?.nomeCupom}
                </Typography>
                {!novo && cupomEdicao && cupomEdicao.quantidadeUtilizada > 0 && (
                    <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                        · usado {cupomEdicao.quantidadeUtilizada}x
                    </Typography>
                )}
                <IconButton className={classes.fechar} onClick={onFechar} disabled={salvando} aria-label="Fechar">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {novo ? (
                    <>
                        <Stepper activeStep={passo} alternativeLabel style={{ padding: "0 0 24px" }}>
                            {PASSOS.map(p => (
                                <Step key={p}>
                                    <StepLabel>{p}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        {passo === 0 && (
                            <SecaoCodigo cupom={cupom} atualizar={atualizar} erro={erroCodigo} disabled={disabled} autoFocus />
                        )}
                        {passo === 1 && (
                            <SecaoDesconto cupom={cupom} atualizar={atualizar} setCupom={setCupom} disabled={disabled} />
                        )}
                        {passo === 2 && (
                            <>
                                <SecaoRegras
                            cupom={cupom}
                            atualizar={atualizar}
                            disabled={disabled}
                            fimSalvo={cupomEdicao?.fimCupomTimestamp}
                        />
                                <Box mt={2}>
                                    <SecaoAvancadas cupom={cupom} atualizar={atualizar} disabled={disabled} />
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {cupomEdicao && estaExpirado(cupomEdicao) && (
                            <Box className={classes.avisoExpirado}>
                                <Typography variant="body2">
                                    <strong>Cupom expirado</strong> em {formatarData(cupomEdicao.fimCupomTimestamp as number)}.
                                    Para reativá-lo, escolha uma nova validade e salve.
                                </Typography>
                            </Box>
                        )}
                        <SecaoCodigo cupom={cupom} atualizar={atualizar} erro={erroCodigo} disabled={disabled} />
                        <SecaoDesconto cupom={cupom} atualizar={atualizar} setCupom={setCupom} disabled={disabled} />
                        <SecaoRegras
                            cupom={cupom}
                            atualizar={atualizar}
                            disabled={disabled}
                            fimSalvo={cupomEdicao?.fimCupomTimestamp}
                        />
                        <Box mt={2}>
                            <SecaoAvancadas cupom={cupom} atualizar={atualizar} disabled={disabled} />
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions className={classes.acoes}>
                {!novo && !confirmandoExclusao && (
                    <Button
                        variant="outlined"
                        size="small"
                        className={classes.botaoExcluir}
                        startIcon={<Delete />}
                        onClick={() => setConfirmandoExclusao(true)}
                        disabled={disabled}
                    >
                        Excluir
                    </Button>
                )}
                {confirmandoExclusao ? (
                    <>
                        <Typography variant="body2" style={{ marginRight: "auto", color: "var(--cor-erro)" }}>
                            Excluir este cupom?
                        </Typography>
                        <Button onClick={() => setConfirmandoExclusao(false)} disabled={salvando}>
                            Não
                        </Button>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: "var(--cor-erro)", color: "white", borderRadius: 20 }}
                            onClick={handleExcluir}
                            disabled={salvando}
                        >
                            Sim, excluir
                        </Button>
                    </>
                ) : (
                    <>
                        <Box flexGrow={1} />
                        {novo && passo > 0 ? (
                            <Button onClick={() => setPasso(p => p - 1)} disabled={salvando}>
                                Voltar
                            </Button>
                        ) : (
                            <Button onClick={onFechar} disabled={salvando}>
                                Cancelar
                            </Button>
                        )}
                        {novo && passo < PASSOS.length - 1 ? (
                            <Button className={classes.botaoPrimario} onClick={avancar} disabled={disabled}>
                                Próximo
                            </Button>
                        ) : (
                            <Button className={classes.botaoPrimario} onClick={handleSalvar} disabled={disabled}>
                                {novo ? "Criar cupom" : "Salvar"}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}
